import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { usersApi } from '../services/apiClient';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user permissions from the API
  const fetchPermissions = async () => {
    try {
      if (!user?.id) {
        // Set default permissions for unauthenticated users
        setPermissions([
          'login'
        ]);
        setLoading(false);
        return;
      }

      const response = await usersApi.getMyPermissions();

      // API can return permissions in two ways:
      // 1. { success: true, permissions: [...] } - flat array (expected from backend)
      // 2. { success: true, user: { permissions: { custom: [...], default: [...] } } } - nested object (actual response)
      let permissionsArray = [];

      if (response.data.success) {
        if (response.data.permissions && Array.isArray(response.data.permissions)) {
          // Case 1: Flat array at root
          permissionsArray = response.data.permissions;
        } else if (response.data.user?.permissions) {
          // Case 2: Nested structure in user object
          const perms = response.data.user.permissions;
          if (typeof perms === 'object' && !Array.isArray(perms)) {
            // Combine default and custom permissions
            permissionsArray = [
              ...(perms.default || []),
              ...(perms.custom || [])
            ];
          } else if (Array.isArray(perms)) {
            // Already a flat array
            permissionsArray = perms;
          }
        }

        setPermissions(permissionsArray);
      } else {
        throw new Error('Invalid permissions response');
      }
      setError(null);
    } catch (err) {
      setError('Failed to fetch permissions');
      // Set default permissions as fallback
      setPermissions([
        'login',
        'devices.view',
        'locations.view',
        'tickets.view',
        'tickets.create'
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has specific permission
  const hasPermission = (resourceOrPermission, action) => {
    if (!user) return false;

    // Admin users have all permissions
    if (user.role === 'admin') return true;

    // Handle both formats: "locations.view" or ("locations", "view")
    let permission;

    if (action !== undefined) {
      // Called with two arguments (old format): hasPermission("locations", "view")
      permission = `${resourceOrPermission}.${action}`;
    } else {
      // Called with one argument (new format): hasPermission("locations.view")
      permission = resourceOrPermission;
    }

    // Check if permission exists in the flat permissions array
    if (Array.isArray(permissions)) {
      return permissions.includes(permission);
    }

    return false;
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionChecks) => {
    // Support both formats: array of strings or array of tuples
    if (Array.isArray(permissionChecks) && permissionChecks.length > 0) {
      if (Array.isArray(permissionChecks[0])) {
        // Array of tuples: [["resource", "action"], ...]
        return permissionChecks.some(([resource, action]) => hasPermission(resource, action));
      } else {
        // Array of strings: ["permission1", "permission2", ...]
        return permissionChecks.some(perm => hasPermission(perm));
      }
    }
    return false;
  };

  // Check if user has all specified permissions
  const hasAllPermissions = (permissionChecks) => {
    // Support both formats: array of strings or array of tuples
    if (Array.isArray(permissionChecks) && permissionChecks.length > 0) {
      if (Array.isArray(permissionChecks[0])) {
        // Array of tuples: [["resource", "action"], ...]
        return permissionChecks.every(([resource, action]) => hasPermission(resource, action));
      } else {
        // Array of strings: ["permission1", "permission2", ...]
        return permissionChecks.every(perm => hasPermission(perm));
      }
    }
    return false;
  };

  // Refresh permissions
  const refreshPermissions = () => {
    fetchPermissions();
  };

  // Initialize permissions on mount or when user changes
  useEffect(() => {
    fetchPermissions();
  }, [user?.id]);

  // Add backward compatibility properties and helper functions
  const canManageDevices = hasPermission('devices.edit') || hasPermission('devices.delete');
  const canManageLocations = hasPermission('locations.edit') || hasPermission('locations.delete');
  const canManageUsers = hasPermission('users.permissions.manage');

  const value = {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions,
    canManageDevices, // For backward compatibility
    canManageLocations,
    canManageUsers,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;